import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@/services/api/queryKeys';
import {
  academicTermsApi,
  examinationTypesApi,
  gradingScalesApi,
} from '@/services/api/endpoints/examinations';
import { useAuth } from '@/features/auth/useAuth';
import { useWorkingContext } from '@/hooks/useWorkingContext';
import { PERMISSIONS } from '@/types/permissions';
import type { ExaminationStatus } from '@/types/examination';

/**
 * Reference data every examination screen needs, and the lifecycle rules the UI
 * mirrors.
 *
 * The transition map is a copy of the backend's `ExaminationStatus`, kept here
 * so the buttons offered are the ones that will actually be accepted. It is
 * presentation only — the backend refuses an illegal step regardless of what
 * this file says, and it stays the authority.
 */

const REFERENCE_OPTIONS = { staleTime: 5 * 60_000 } as const;

export function useExaminationTypes() {
  const { can } = useAuth();
  return useQuery({
    queryKey: queryKeys.examinationTypes.all,
    queryFn: examinationTypesApi.list,
    enabled: can(PERMISSIONS.EXAMINATION_VIEW),
    ...REFERENCE_OPTIONS,
  });
}

export function useGradingScales() {
  const { can } = useAuth();
  return useQuery({
    queryKey: queryKeys.gradingScales.all,
    queryFn: gradingScalesApi.list,
    enabled: can(PERMISSIONS.GRADING_SCALE_VIEW),
    ...REFERENCE_OPTIONS,
  });
}

/** Terms for the working context's year. Null year means the pickers have not resolved yet. */
export function useTerms(academicYearId?: string | null) {
  const { can } = useAuth();
  const context = useWorkingContext();
  const yearId = academicYearId ?? context.academicYearId;
  return useQuery({
    queryKey: queryKeys.academicTerms.forYear(yearId ?? 'none'),
    queryFn: () => academicTermsApi.forYear(yearId as string),
    enabled: yearId !== null && yearId !== undefined && can(PERMISSIONS.ACADEMIC_YEAR_VIEW),
    ...REFERENCE_OPTIONS,
  });
}

export const EXAMINATION_STATUSES: { value: ExaminationStatus; label: string }[] = [
  { value: 'DRAFT', label: 'Draft' },
  { value: 'SCHEDULED', label: 'Scheduled' },
  { value: 'ONGOING', label: 'Ongoing' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'RESULTS_PROCESSING', label: 'Results processing' },
  { value: 'PUBLISHED', label: 'Published' },
  { value: 'ARCHIVED', label: 'Archived' },
];

/** What each step means, so a button is never just a status name. */
const TRANSITIONS: Record<ExaminationStatus, { to: ExaminationStatus; label: string; hint: string }[]> = {
  DRAFT: [
    {
      to: 'SCHEDULED',
      label: 'Publish schedule',
      hint: 'Papers and sittings are fixed. Students and staff can be told the dates.',
    },
    { to: 'ARCHIVED', label: 'Archive', hint: 'Files the examination away. Nothing further can be recorded against it.' },
  ],
  SCHEDULED: [
    { to: 'ONGOING', label: 'Start', hint: 'Papers are being sat. Marks entry opens.' },
    { to: 'DRAFT', label: 'Return to draft', hint: 'Reopens papers and dates for editing.' },
    { to: 'ARCHIVED', label: 'Archive', hint: 'Files the examination away.' },
  ],
  ONGOING: [
    { to: 'COMPLETED', label: 'Mark complete', hint: 'Every paper has been sat.' },
    { to: 'SCHEDULED', label: 'Back to scheduled', hint: 'Closes marks entry again.' },
    { to: 'ARCHIVED', label: 'Archive', hint: 'Files the examination away.' },
  ],
  COMPLETED: [
    {
      to: 'RESULTS_PROCESSING',
      label: 'Process results',
      hint: 'Needs every marks sheet approved.',
    },
    { to: 'ONGOING', label: 'Reopen marking', hint: 'Marks entry opens again.' },
    { to: 'ARCHIVED', label: 'Archive', hint: 'Files the examination away.' },
  ],
  RESULTS_PROCESSING: [
    { to: 'PUBLISHED', label: 'Publish results', hint: 'Releases results to report cards.' },
    { to: 'COMPLETED', label: 'Back to completed', hint: 'Marks can be corrected again.' },
    { to: 'ARCHIVED', label: 'Archive', hint: 'Files the examination away.' },
  ],
  /*
   * Leaving PUBLISHED is a withdrawal, and withdrawal goes through the results
   * screen's unpublish action rather than a status change — the timestamps have
   * to be cleared along with the status, which only that endpoint does.
   */
  PUBLISHED: [{ to: 'ARCHIVED', label: 'Archive', hint: 'Files the examination away.' }],
  ARCHIVED: [],
};

export function transitionsFor(status: ExaminationStatus) {
  return TRANSITIONS[status];
}

/** Marks and results are read-only once an examination is published or archived. */
export function isLocked(status: ExaminationStatus): boolean {
  return status === 'PUBLISHED' || status === 'ARCHIVED';
}

/**
 * Whether the calculate button is worth offering. A mirror of the backend's
 * `canCalculateResults`, which still refuses anything else outright.
 */
export function canCalculateResults(status: ExaminationStatus): boolean {
  return status === 'COMPLETED' || status === 'RESULTS_PROCESSING';
}

/** Marks entry is only open while papers are being sat or immediately after. */
export function acceptsMarks(status: ExaminationStatus): boolean {
  return status === 'ONGOING' || status === 'COMPLETED';
}
